import { Box, ButtonBase, Chip, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { getCityImage, cityGradient } from '../../assets/cityImages';
import type { City } from '../../types';

type Props = {
  city: City;
  /** Small label that appears above the city name, e.g. "Discover our hair stylists". */
  overline: string;
  onClick: () => void;
};

export function CityCard({ city, overline, onClick }: Props) {
  const image = getCityImage(city.slug);
  const gradient = cityGradient(city.slug);

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        height: '100%',
        display: 'block',
        textAlign: 'left',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        aspectRatio: '4 / 3',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(0,0,0,0.45)'
            : '0 8px 24px rgba(26,24,22,0.10)',
        transition: 'transform 280ms ease, box-shadow 280ms ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 18px 40px rgba(0,0,0,0.6)'
              : '0 18px 40px rgba(26,24,22,0.18)',
        },
        '&:hover .city-bg': { transform: 'scale(1.06)' },
        '&:hover .city-arrow': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      {/* Backdrop — image if available, else stable gradient */}
      {image ? (
        <Box
          className="city-bg"
          component="img"
          src={image}
          alt={city.name}
          loading="lazy"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 600ms ease',
          }}
        />
      ) : (
        <Box
          className="city-bg"
          sx={{
            position: 'absolute',
            inset: 0,
            background: gradient,
            transition: 'transform 600ms ease',
          }}
        >
          {/* Subtle radial highlight to add depth to the gradient */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(70% 60% at 25% 20%, rgba(255,255,255,0.18) 0%, transparent 60%)',
            }}
          />
          <LocationOnIcon
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 80,
              color: 'rgba(255,255,255,0.12)',
            }}
          />
        </Box>
      )}

      {/* Bottom gradient for legibility */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.20) 50%, rgba(0,0,0,0.78) 100%)',
        }}
      />

      {/* Country chip top-right */}
      <Chip
        label={city.country}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          bgcolor: 'rgba(255,255,255,0.20)',
          color: 'common.white',
          backdropFilter: 'blur(6px)',
          fontWeight: 700,
          letterSpacing: '0.05em',
          height: 26,
        }}
      />

      {/* Text content + arrow */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: { xs: 2, sm: 2.5 },
          color: 'common.white',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.14em',
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.5,
            }}
            noWrap
          >
            {overline}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              lineHeight: 1.15,
              textShadow: '0 2px 8px rgba(0,0,0,0.4)',
            }}
            noWrap
          >
            {city.name}
          </Typography>
        </Box>
        <Box
          className="city-arrow"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'common.white',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            opacity: { xs: 1, sm: 0 },
            transform: { xs: 'translateX(0)', sm: 'translateX(-8px)' },
            transition: 'all 280ms ease',
          }}
        >
          <ArrowForwardRoundedIcon sx={{ fontSize: '1.25rem' }} />
        </Box>
      </Box>
    </ButtonBase>
  );
}
